import { faArrowDownAZ, faDownload, faWarning } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DataTable } from "@ventumsoftware/react-toolkit";
import moment from "moment/moment";
import React, { type FC, useMemo, useState } from "react";
import { useApi } from "../api/APIContext";
import { useApiCall } from "../api/api";
import { type Certificate, Exam } from "../api/dto";
import { ContentContainer } from "../components/content/ContentContainer";
import { PageHeader } from "../components/content/PageHeader";
import { SearchReload } from "../components/data/SearchAndReload";
import { TablePlaceholder } from "../components/data/TablePlaceholder";
import { PillSelect } from "../components/form/PillSelect";
import { Select } from "../components/form/Select";

export const CertificatesPage: FC<{}> = (props) => {
  const [search, setSearch] = useState("");
  const { exams, projects, companies, questionSets, certificates, loading } = useApi();
  const { downloadCertificateUrl } = useApiCall();
  const [questionSet, setQuestionSet] = useState("Alle");

  // const [selectedStatusFilter, setSelectedStatusFilter] = useState<keyof typeof statusFilters>('Alle')
  // const [sort, setSort] = usePersistentState<keyof typeof sortOptions>(`exam-overview-sort`, 'nameDesc')

  const filteredCertificates = useMemo(() => {
    return certificates.filter((certificate) => {
      if (questionSet === "Alle") {
        return true;
      }
      if (questionSet === "Rct") {
        return (
          certificate.questionSetId !== null &&
          questionSets
            .filter((qs) => qs.type === "Rct")
            .map((qs) => qs.id)
            .includes(certificate.questionSetId)
        );
      }
      if (questionSet === "Fct") {
        return (
          certificate.questionSetId !== null &&
          questionSets
            .filter((qs) => qs.type === "Fct")
            .map((qs) => qs.id)
            .includes(certificate.questionSetId)
        );
      }
      if (questionSet === "Fir") {
        return (
          certificate.questionSetId !== null &&
          questionSets
            .filter((qs) => qs.type === "Fir")
            .map((qs) => qs.id)
            .includes(certificate.questionSetId)
        );
      }
      return certificate.questionSetId === questionSet;
    });
  }, [questionSet, questionSets, certificates]);

  const questionSetFilterOptions = useMemo(() => {
    return Object.fromEntries([
      ["Alle", "Alle"],
      ["Rct", "Rct - Alle"],
      ...questionSets.filter((q) => q.type === "Rct").map((q) => [q.id, `Rct - ${q.name}`]),
      ["Fct", "Fct - Alle"],
      ...questionSets.filter((q) => q.type === "Fct").map((q) => [q.id, `Fct - ${q.name}`]),
      ["Fir", "Fir - Alle"],
      ...questionSets.filter((q) => q.type === "Fir").map((q) => [q.id, `Fir - ${q.name}`]),
    ]);
  }, [questionSets]);

  return (
    <ContentContainer size={"md"}>
      <PageHeader>Certificaten</PageHeader>

      <div className={"grid grid-cols-4"}>
        <Select
          label={"Vragenlijst"}
          options={questionSetFilterOptions}
          value={questionSet}
          onChange={setQuestionSet}
        />
      </div>

      <SearchReload<Certificate>
        data={filteredCertificates}
        filterProperties={[
          "name",
          "formattedTestDate",
          "project",
          "contractor",
          "questionSet",
          "company",
        ]}
        overrideSearch={search}
        overrideOnSearchChange={setSearch}
        resultsBuilder={(results) => {
          return (
            <div>
              {!loading && results.length === 0 ? (
                <TablePlaceholder>Geen examens in selectie</TablePlaceholder>
              ) : (
                <DataTable
                  data={results}
                  compact={true}
                  keyProperty={"id"}
                  loading={loading}
                  pageSize={50}
                  columns={[
                    { header: "Testdatum", sort: true, property: "formattedTestDate" },
                    { header: "Naam", sort: true, property: "name" },
                    { header: "Project", sort: true, property: "project" },
                    { header: "Bedrijf", sort: true, property: "company" },
                    { header: "Contractor", sort: true, property: "contractor" },
                    {
                      header: "Vragenlijst",
                      sort: true,
                      property: "questionSet",
                      transform: (questionSetName) => questionSetName.split(" ")[0],
                    },
                    {
                      header: "Score",
                      sort: true,
                      property: "score",
                      transform: (score, row) => score + " - " + row.scoreClassification,
                    },
                    // {header: "Project", property: 'projectId', transform: (projectId) => {
                    //   const project = projects.find(project => project.id === projectId);
                    //   if (!project) return <div className={'text-red-600 font-medium flex items-center'}><FontAwesomeIcon icon={faWarning} className={'mr-1'} /><span>Geen</span></div>;
                    //   if (project.isArchived) return <div className={'text-amber-600 font-medium flex items-center'}><FontAwesomeIcon icon={faWarning} className={'mr-1'} /><span>{project.name}</span></div>;
                    //   return project.name;
                    // }},
                    // {header: "Status", property: 'status'},
                  ]}
                  onClickActions={[
                    {
                      icon: faDownload,
                      variant: "primary",
                      text: "Download",
                      onClick: async (certificate) => {
                        const url = await downloadCertificateUrl(certificate.id);
                        const link = document.createElement("a");
                        link.href = url;
                        link.download = certificate.downloadFileName;
                        link.target = "_blank";
                        link.click();
                      },
                    },
                  ]}
                />
              )}
            </div>
          );
        }}
      />
    </ContentContainer>
  );
};
